// Incoming-call bus — module-level external store (useSyncExternalStore)
// shared by the fullscreen CallAlertOverlay, the tab-title flasher and
// the system notification. Single source of truth for "a call is
// ringing", with a `ringId` so a dismissed alert stays dismissed for
// THAT ring but re-appears for the next one.
//
// Fed from two places:
//  • VoipLauncher forwards every `pkv:phase` broadcast from the widget
//    (phase + caller identity parsed from the SIP display name).
//  • VoipPanel's lookupCaller handler mirrors the CRM lookup result so
//    the overlay can show the contact name + recent affaires without a
//    second request.
//
// Ported from V2 (50_Webapp features/voip/incoming-call-bus.ts).

export interface IncomingAffaire {
  id: number | string
  status?: string | null
  address?: string | null
  active?: boolean
}

export interface IncomingMatch {
  name: string
  affaires: IncomingAffaire[]
}

export interface IncomingCallState {
  ringing: boolean
  /** Increments on each idle→incoming edge; used for per-ring dismissal. */
  ringId: number
  number: string | null
  remoteName: string | null
  /** Dossier keyed in by the caller at the IVR, if any. */
  ivrAffaire: string | null
  matches: IncomingMatch[]
}

export interface VoipPhaseDetail {
  phase: 'idle' | 'dialing' | 'incoming' | 'active'
  status: 'available' | 'away'
  registered: boolean
  incoming: {
    number: string
    remoteName?: string
    affaire?: string
  } | null
}

const initial: IncomingCallState = {
  ringing: false,
  ringId: 0,
  number: null,
  remoteName: null,
  ivrAffaire: null,
  matches: [],
}

let state: IncomingCallState = initial
const listeners = new Set<() => void>()

function emit(next: IncomingCallState): void {
  state = next
  listeners.forEach((l) => l())
}

export function subscribeIncomingCall(cb: () => void): () => void {
  listeners.add(cb)
  return () => listeners.delete(cb)
}

export function getIncomingCall(): IncomingCallState {
  return state
}

const last9 = (n: string | null | undefined): string =>
  String(n ?? '').replace(/\D/g, '').slice(-9)

export function noteVoipPhase(detail: VoipPhaseDetail): void {
  if (detail.phase === 'incoming' && detail.incoming) {
    const sameRing = state.ringing && last9(state.number) === last9(detail.incoming.number)
    emit({
      ringing: true,
      ringId: sameRing ? state.ringId : state.ringId + 1,
      number: detail.incoming.number,
      remoteName: detail.incoming.remoteName ?? null,
      ivrAffaire: detail.incoming.affaire ?? null,
      // Keep matches when it's the same ring (lookup may land after
      // several phase re-broadcasts); reset on a new ring.
      matches: sameRing ? state.matches : [],
    })
    return
  }
  if (state.ringing) {
    emit({ ...initial, ringId: state.ringId })
  }
}

export function noteCallerLookup(number: string, matches: IncomingMatch[]): void {
  if (!state.ringing) return
  if (last9(number) !== last9(state.number)) return
  emit({ ...state, matches })
}
